<template>
  <Popover ref="popover" :showHeader="true" :headerTitle="title" sizeClass="popover-medium">
    <LoadingPanel ref="loadingPanel" />

    <div class="order-list">
      <div v-for="(group, groupIndex) in orderGroupList" :key="groupIndex">
        <h4 class="my-0">{{group.title}}</h4>
        <div v-for="order in group.orders" :key="order.id" class="order-list-item">
          <div class="float-right">
            <ExpandCollapseButton v-model="order.expanded" />
          </div>
          <Drag :data="{dragType:'order', orderId: order.id}" type="task-manager-order" :disabled="!orderHasUnassigned(order) || !draggableSupported">
            <p :class="{'order-list-title': true, 'draggable-item': orderHasUnassigned(order) && draggableSupported}">
              <i class="far fa-circle tasks-order-icon-no-assigned" v-if="orderHasAllUnassigned(order)"></i>
              <i class="far fa-circle tasks-order-icon-tasks-progress" v-if="orderTasksInProgress(order) || (!orderHasAllUnassigned(order) && orderHasUnassigned(order) && !orderTasksAllPending(order))"></i>
              <i class="far fa-circle tasks-order-icon-tasks-completed" v-if="orderTasksAllComplete(order) && !orderHasUnassigned(order)"></i>
              <i class="far fa-circle tasks-order-icon-some-assigned" v-if="orderHasAssigned(order) && orderTasksAllPending(order)"></i>
              #{{order.sequenceNumber}} - {{order.contact.name}}
            </p>
          </Drag>
          <div class="pl-1">
            <div v-show="order.expanded">
              <p class="text-muted">
                <i class="fas fa-fw fa-shopping-cart"></i>&nbsp;<LinkOrder :id="order.id" :name="`Order #${order.sequenceNumber}`" />
              </p>
              <p class="text-muted" v-if="order.customerRef"> 
                <i class="fas fa-fw fa-tag"></i>&nbsp;{{order.customerRef}}
              </p>
              <p class="text-muted">
                <i class="fas fa-fw fa-clock"></i>&nbsp;{{order.dateCurrentlyDue | formatDateTime}}
              </p>
              <p v-if="order.deliveryTag && order.deliveryTag.length > 0">
                <i class="fas fa-fw fa-truck"></i>&nbsp;<LabelTags :labelList="order.deliveryTag" class="d-inline-block" />
              </p>

              <div class="my-2 pl-1">
                <template v-if="order.unassignedItems.length > 0">
                  <strong>Unassigned</strong>
                  <div class="mb-1 pl-1">
                    <Drag :data="{dragType:'orderItem', orderId: order.id, orderItemId: orderItem.id}" type="task-manager-order" v-for="orderItem of order.unassignedItems" :key="orderItem.id" :disabled="!draggableSupported">
                      <p
                        :class="{'tasks-order-item': true, 'tasks-order-item-draggable': draggableSupported, 'draggable-item': draggableSupported}"
                        data-dragtype="orderItem"
                        :data-order-id="order.orderId"
                        :data-order-item-id="orderItem.id">

                        <i class="fas fa-caret-right mr-1"></i>
                        {{orderItem.quantity}}x {{orderItemDescription(orderItem)}} - {{orderItem.glassHeight}}x{{orderItem.glassWidth}}
                      </p>
                    </Drag>
                  </div>
                </template>
                <template v-if="order.assignedItems.length > 0">
                  <strong>Assigned</strong>
                  <div class="pl-1">
                    <p
                      v-for="orderItem of order.assignedItems"
                      :key="orderItem.id"
                      :class="{'tasks-order-item': true}"
                      data-dragtype="orderItem"
                      :data-order-id="order.orderId"
                      :data-order-item-id="orderItem.id">

                      <i class="fas fa-caret-right mr-1"></i>
                      {{orderItem.quantity}}x {{orderItemDescription(orderItem)}} - {{orderItem.glassHeight}}x{{orderItem.glassWidth}}
                      <template v-if="orderItem.taskItems">
                        <small class="font-weight-bold" v-for="(taskItem, itemIndex) of orderItem.taskItems" :key="taskItem.id" v-show="isTaskActivePending(taskItem.task)">
                          {{itemIndex == 0 ? '' : ', '}}{{taskItem.task.sequenceNumber}}
                        </small>
                      </template>
                    </p>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
  </Popover>
</template>

<style lang="scss" scoped>
.order-list {
  padding: 0.25rem;
  font-size: 13px;
  max-height: 40vh;
  overflow-y: scroll;
}
.order-list-title {
  width: calc(100% - 30px);
  font-weight: bold;
  padding: 3px;
}
.order-list-item {
  padding-bottom: 0.3rem;
}
.order-list .order-list-item:not(:first-child) {
  padding-top: 0.4rem;
}
.order-list-item p {
  margin: 0 0 0.2rem 0;
}
</style>

<script>
import { Drag } from 'vue-easy-dnd'

import TaskManagerMixin from '@/modules/tasks/mixins/taskManagerMixin'
import Popover from '@/components/popover'

import { TaskStatusTypes, TaskItemStatusTypes } from '@/store/types'

export default {
  name: 'MapOrdersPopover',
  components: {
    Drag,
    Popover
  },
  mixins: [TaskManagerMixin],
  props: {
    dragEnabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      orderIdList: [],
      orderList: [],
      orderGroupList: []
    }
  },
  computed: {
    title() {
      return 'Orders'
    },
    draggableSupported() {
      return this.dragEnabled && this.userHasCreatePermission(this.PermissionTypes.TaskPermissionTypes.task) && this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task)
    }
  },
  methods: {
    displayFromElement(element, ordersList) {
      this.$refs.popover.displayFromElement(element)

      // Reset
      this.orderList = []
      this.orderGroupList = []
      this.orderIdList = ordersList.map(order => order.id)

      this.requestData()
    },
    requestData() {
      this.$refs.loadingPanel.startLoading()

      this.$apiService.tasksService.getTaskOrderList(this.orderIdList)
      .then(response => {

        this.orderList = response.data.map(order => {
          order.expanded = false

          // Add in assigned/unassigned details
          order.unassignedItems = order.orderItems.filter(orderItem => {
            var waitingTaskItemCount = orderItem.taskItems.filter(taskItem => {
              if(taskItem.task && (taskItem.task.status == TaskStatusTypes.cancelled || taskItem.task.status == TaskStatusTypes.completed))
                return false
              return taskItem.status == TaskItemStatusTypes.pending || taskItem.status == TaskItemStatusTypes.active
            }).length
            
            return waitingTaskItemCount == 0
          })
          order.assignedItems = order.orderItems.filter(orderItem => {
            var waitingTaskItemCount = orderItem.taskItems.filter(taskItem => {
              if(taskItem.task && (taskItem.task.status == TaskStatusTypes.cancelled || taskItem.task.status == TaskStatusTypes.completed))
                return false

              return taskItem.status == TaskItemStatusTypes.pending || taskItem.status == TaskItemStatusTypes.active
            }).length
            
            return waitingTaskItemCount > 0
          })

          return order
        })

        // Group orders into assigned/unassigned/partially assigned
        let unassignedList = this.orderList.filter(order => order.assignedItems.length == 0)
        let partialList = this.orderList.filter(order => order.unassignedItems.length > 0 && order.assignedItems.length > 0)
        let assignedList = this.orderList.filter(order => order.unassignedItems.length == 0 && order.assignedItems.length > 0)

        if(unassignedList.length > 0)
          this.orderGroupList.push({ title: 'Unassigned Orders', orders: unassignedList })
        if(partialList.length > 0)
          this.orderGroupList.push({ title: 'Partially Assigned Orders', orders: partialList })
        if(assignedList.length > 0)
          this.orderGroupList.push({ title: 'Assigned Orders', orders: assignedList })

      })
      .catch(err => {
        this.$refs.loadingPanel.setError('Error loading orders', err)
      })
      .finally(() => {
        this.$refs.loadingPanel.finishedLoading()
      })
    },

    // Check if the order is all assigned
    orderHasAllAssigned(order) {
      return order.unassignedItems.length == 0 && order.assignedItems.length > 0
    },
    orderHasAllUnassigned(order) {
      return order.unassignedItems.length > 0 && order.assignedItems.length == 0
    },

    // Check if the order has some unassigned items
    orderHasUnassigned(order) {
      return order.unassignedItems.length > 0
    },
    // Check if the order has some assigned items
    orderHasAssigned(order) {
      return order.assignedItems.length > 0
    },

    // Check if an order has tasks in progress
    orderTasksInProgress(order) {
      let orderItems = order.orderItems || []
      return orderItems.filter(orderItem => {
        if(orderItem.taskItems) {
          return orderItem.taskItems.filter(taskItem => taskItem.status == TaskItemStatusTypes.active).length > 0
        }
        return false
      }).length > 0      
    },
    // Check if all tasks associated with an order are pending
    orderTasksAllPending(order) {
      let orderItems = order.orderItems || []
      return orderItems.filter(orderItem => {
        if(orderItem.taskItems) {
          return orderItem.taskItems.filter(taskItem => taskItem.status == TaskItemStatusTypes.pending).length == orderItem.taskItems.length
        }
        return false
      }).length == orderItems.length
    },
    // Check if all tasks associated with an order are pending / active
    orderTasksAllPendingActive(order) {
      let orderItems = order.orderItems || []
      return orderItems.filter(orderItem => {
        if(orderItem.taskItems) {
          return orderItem.taskItems.filter(taskItem => taskItem.status == TaskItemStatusTypes.pending || taskItem.status == TaskItemStatusTypes.active).length == orderItem.taskItems.length
        }
        return false
      }).length == orderItems.length
    },
    // Check if all tasks associated with an order are completed
    orderTasksAllComplete(order) {
      let orderItems = order.orderItems || []
      return orderItems.filter(orderItem => {
        if(orderItem.taskItems) {
          return orderItem.taskItems.filter(taskItem => taskItem.status == TaskItemStatusTypes.completed).length == orderItem.taskItems.length && orderItem.taskItems.length > 0
        }
        return false
      }).length == orderItems.length && orderItems.length > 0
    }


  }
}
</script>